/**
 * Refined-modern UI kit.
 * Shared building blocks extracted from Landing / Login / Signup / Dashboard.
 * Use these so every page stays pixel-consistent and on-brand.
 *
 * Palette:  navy #0F172A · blue #0025D8 (hover #001CB3) · purple #7C3AED
 * Radius:   cards rounded-2xl · controls rounded-xl · pills rounded-full
 */
import React from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

/* ── Layout ─────────────────────────────────────────────────── */

export function PageContainer({ children, className, width = "wide" }) {
  const max = {
    full: "max-w-none", wide: "max-w-7xl", lg: "max-w-6xl", mid: "max-w-4xl", prose: "max-w-3xl",
    sm: "max-w-2xl", narrow: "max-w-xl", xs: "max-w-md",
  }[width] || "max-w-7xl";
  return <main className={cn(max, "mx-auto px-6 md:px-12 py-12", className)}>{children}</main>;
}

export function Eyebrow({ children, className, tone = "blue" }) {
  const c = { blue: "text-[#0025D8]", muted: "text-muted-foreground", purple: "text-[#7C3AED]" }[tone];
  return <div className={cn("font-mono text-[10px] tracking-[0.25em] uppercase", c, className)}>{children}</div>;
}

export function SectionLabel({ children, className }) {
  return <div className={cn("font-mono text-[10px] text-muted-foreground tracking-[0.2em] uppercase mb-3", className)}>{children}</div>;
}

export function BackLink({ to, children = "Back" }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-muted-foreground hover:text-[#0025D8] transition-colors group">
      <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> {children}
    </Link>
  );
}

/**
 * Standard page header — eyebrow + title + subtitle on the left, actions on the right.
 * `back` renders a BackLink above the title.
 */
export function PageHeader({ eyebrow, title, subtitle, actions, back, children, className }) {
  return (
    <div className={cn("flex items-start justify-between gap-4 flex-wrap mb-8", className)}>
      <div>
        {back && <div className="mb-2"><BackLink to={back.to}>{back.label}</BackLink></div>}
        {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-sm mt-1.5 max-w-2xl">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ── Surfaces ───────────────────────────────────────────────── */

export function Card({ children, className, hover, padding = "p-6", ...props }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-white", padding,
        hover && "hover:border-[#0025D8]/30 hover:shadow-lg hover:shadow-[#0F172A]/5 hover:-translate-y-0.5 transition-all",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Navy gradient hero card (smart-hero / CTA style). */
export function HeroCard({ children, className }) {
  return (
    <div className={cn("relative overflow-hidden rounded-2xl bg-[#0F172A] text-white p-7 md:p-8", className)}>
      <div className="pointer-events-none absolute -top-24 -right-20 w-80 h-80 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.22) 0%, rgba(37,99,235,0.12) 45%, transparent 70%)" }} />
      <div className="relative">{children}</div>
    </div>
  );
}

/* ── Stats ──────────────────────────────────────────────────── */

export function StatChip({ value, label, accent }) {
  return (
    <div className={cn("inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1", accent ? "border-[#0025D8]/25 bg-[#0025D8]/5" : "border-border bg-white")}>
      <span className={cn("font-display font-bold text-sm", accent ? "text-[#0025D8]" : "text-[#0F172A]")}>{value}</span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

export function StatCard({ value, label, color }) {
  return (
    <div className="rounded-xl bg-secondary/40 border border-border p-4 text-center">
      <div className="font-display font-black text-2xl" style={color ? { color } : undefined}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────── */

const BTN_VARIANTS = {
  primary:   "bg-[#0025D8] hover:bg-[#001CB3] text-white shadow-lg shadow-[#0025D8]/20",
  navy:      "bg-[#0F172A] hover:bg-[#1E293B] text-white shadow-lg shadow-[#0F172A]/20",
  purple:    "bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-lg shadow-[#7C3AED]/20",
  white:     "bg-white text-[#0F172A] hover:bg-white/90 shadow-lg",
  secondary: "border border-border bg-white text-[#0F172A] hover:bg-secondary",
  ghost:     "text-[#0F172A] hover:bg-secondary",
  danger:    "bg-[#E11D48] hover:bg-[#be1238] text-white",
  outlineDanger: "border border-border bg-white text-[#0F172A] hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48]",
};
const BTN_SIZES = { sm: "px-3 py-2 text-xs", md: "px-5 py-2.5 text-sm", lg: "px-6 py-3 text-base" };

/** Polymorphic button — pass `to` to render a react-router Link. */
export function Button({ variant = "primary", size = "md", className, to, loading, disabled, children, ...props }) {
  const cls = cn(
    "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed",
    BTN_VARIANTS[variant] || BTN_VARIANTS.primary,
    BTN_SIZES[size] || BTN_SIZES.md,
    className
  );
  const inner = <>{loading && <Loader2 className="w-4 h-4 animate-spin" />}{children}</>;
  if (to && !disabled) return <Link to={to} className={cls} {...props}>{inner}</Link>;
  return <button className={cls} disabled={disabled || loading} {...props}>{inner}</button>;
}

/* ── Forms ──────────────────────────────────────────────────── */

export const inputClass =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all";

export function Input({ className, ...props }) {
  return <input className={cn(inputClass, className)} {...props} />;
}

export function Label({ children, optional, className }) {
  return (
    <label className={cn("block text-xs font-semibold text-[#0F172A] mb-1.5", className)}>
      {children}
      {optional && <span className="ml-2 font-normal text-muted-foreground/60">optional</span>}
    </label>
  );
}

export function Field({ label, optional, hint, children, className }) {
  return (
    <div className={className}>
      {label && <Label optional={optional}>{label}</Label>}
      {children}
      {hint && <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

/* ── States ─────────────────────────────────────────────────── */

export function Loading({ label = "Loading…", className }) {
  return (
    <div className={cn("flex items-center justify-center gap-2 p-12 font-mono text-sm text-muted-foreground", className)}>
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}

/**
 * Empty state — dashed card with icon, title, optional body + action.
 * `icon` is a lucide component.
 */
export function EmptyState({ icon: Icon, title, body, action, className }) {
  return (
    <div className={cn("rounded-2xl border border-dashed border-border p-12 text-center", className)}>
      {Icon && (
        <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-4">
          <Icon className="w-6 h-6 text-muted-foreground" />
        </div>
      )}
      {title && <p className="font-semibold text-[#0F172A] mb-1">{title}</p>}
      {body && <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">{body}</p>}
      {action}
    </div>
  );
}

/** Numbered step for "how it works" explainers. */
export function StepItem({ n, color = "#0025D8", title, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg text-white flex items-center justify-center font-mono text-xs font-bold flex-shrink-0" style={{ background: color }}>{n}</div>
      <div>
        <div className="font-semibold text-sm text-[#0F172A]">{title}</div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/* ── Badges ─────────────────────────────────────────────────── */

export function Pill({ children, className, tone = "muted" }) {
  const tones = {
    muted:  "border-border bg-white text-muted-foreground",
    blue:   "border-[#0025D8]/30 bg-[#0025D8]/5 text-[#0025D8]",
    green:  "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]",
    amber:  "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B]",
    red:    "border-[#E11D48]/30 bg-[#E11D48]/10 text-[#E11D48]",
    navy:   "border-transparent bg-[#0F172A] text-white",
  };
  return <span className={cn("inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-md border", tones[tone], className)}>{children}</span>;
}
